import { gql } from '@common/hooks';

export const CurrentMembershipContextQuery = gql`
  query currentMembershipContextQuery {
    currentMembership {
      id
      name
      role
      roleName
      active
      canUseIntelligentScheduling {
        value
      }
      entityType
      isSupplierUser
      saveLastVisitedPage
      canCreateWorkforceGroupUnitRate
      canCreateShiftGaps
      canUploadShiftGaps
      canEditShiftGaps
      canUploadWorkers
      shiftRateEditsPermitted
      hasDashboards
      hasUkgTimecardsSyncEnabled
      integrationsEnabled
      canApprovalsBypass {
        value
      }
      canApprovalsOverrideApprove {
        value
      }
      canApprovalsOverrideReject {
        value
      }
      canBypassTierVisibility {
        value
      }
      canCreateApplications {
        value
      }
      canCreateApprovalChainTemplates {
        value
      }
      canCreateBillingCard {
        value
      }
      canCreateOffers {
        value
      }
      canCreateWorkers {
        value
      }
      canEditIndividualComplianceList {
        value
      }
      canEditWorkers {
        value
      }
      canSeeStaffingRequestRates {
        value
      }
      canUpdateMembership {
        value
      }
      canShowAdminApprovalsItem {
        value
      }
      canShowAdminBilling {
        value
      }
      canShowAdminGroupsTab {
        value
      }
      canShowAdminScheduleCodesTab {
        value
      }
      canUpdateAdminScheduleCodes {
        value
      }
      canShowAdminMembers {
        value
      }
      canShowAdminOrganizationStructure {
        value
      }
      canShowAdminRatesOnDemandTab {
        value
      }
      canShowAdminRatesLongTermTab {
        value
      }
      canShowAdminStaffingRequestTemplates {
        value
      }
      canShowAdminSuppliers {
        value
      }
      canShowAdminTimekeepingHolidays {
        value
      }
      canShowAdminHolidays {
        value
      }
      canShowAdminWorksListTemplates {
        value
      }
      canShowApplications {
        value
      }
      canShowApplicationsSubmitted {
        value
      }
      canShowAssignmentTermBillingRules {
        value
      }
      canShowDisputes {
        value
      }
      canShowDoNotRehires {
        value
      }
      canShowOffers {
        value
      }
      canShowGroupShiftMessaging {
        value
      }
      canShowInvoices {
        value
      }
      canShowTimesheetUploads {
        value
      }
      canShowLongTermAssignmentComments {
        value
      }
      canShowLongTermAssignments {
        value
      }
      canShowMissionControl {
        value
      }
      canShowOrganizationUnitIntegration {
        value
      }
      canShowOrganizationUnitShiftConfigs {
        value
      }
      canShowReverseInvoices {
        value
      }
      canShowShiftTargeting {
        value
      }
      canShowSidebarAdminItem {
        value
      }
      canShowSidebarBillingItem {
        value
      }
      canShowSidebarCandidatesItem {
        value
      }
      canShowSidebarOnboardingItem {
        value
      }
      canShowSidebarStaffingItem {
        value
      }
      canShowSidebarTeamItem {
        value
      }
      canShowSidebarTimesheetsItem {
        value
      }
      canShowTermsConditionsLink {
        value
      }
      canShowShifts {
        value
      }
      canShowTiering {
        value
      }
      canShowWorksLists {
        value
      }
      canShowWorkerStatus {
        value
      }
      canSubmitSubmittalPacketChangeRequest {
        value
      }
      canUpdateAdminUsersUnits {
        value
      }
      canUpdateOffers {
        value
      }
      canUpdateApplications {
        value
      }
      canUpdateStaffingRequests {
        value
      }
      canUpdateWorkerStatus {
        value
      }
      canUpdateStaffingRequestAssignments {
        value
      }
      canWithdrawApplications {
        value
      }
      canShowOrganizationOnDemandSettings {
        value
      }
      canShiftGapTagCreate {
        value
      }
      canShiftGapTagUpdate {
        value
      }
      canUpdateOrganizationBusiness {
        value
      }
      worksUser {
        id
        firstName
        lastName
        phone
        saveLastVisitedPage
        timeZone
        initials
        email
        intercomUserHash
      }
      entity: entityProvider {
        ... on Organization {
          id
          name
          logoAvatarUrl
        }
        ... on Supplier {
          id
          name
        }
      }
    }
  }
`;

export type CurrentMembershipContextQueryResponse = {
  currentMembership: CurrentMembershipPermissionsType;
};

export type MembershipEntityType = {
  id: string;
  name: string;
  logoAvatarUrl?: string;
};

export type CurrentMembershipPermissionsType = {
  id: string;
  name: string;
  role: string;
  roleName: string;
  active: boolean;
  canUseIntelligentScheduling: boolean;
  entityType: string;
  isSupplierUser: boolean;
  saveLastVisitedPage: boolean | null;
  canCreateApplications: boolean;
  canCreateApprovalChainTemplates: boolean;
  canCreateWorkforceGroupUnitRate: boolean;
  canCreateOffers: boolean;
  canCreateShiftGaps: boolean;
  canUploadShiftGaps: boolean;
  canEditShiftGaps: boolean;
  canUploadWorkers: boolean;
  shiftRateEditsPermitted: boolean;
  hasDashboards: boolean;
  hasUkgTimecardsSyncEnabled: boolean;
  integrationsEnabled: [string];
  canApprovalsBypass: boolean;
  canApprovalsOverrideApprove: boolean;
  canApprovalsOverrideReject: boolean;
  canBypassTierVisibility: boolean;
  canCreateBillingCard: boolean;
  canCreateWorkers: boolean;
  canEditIndividualComplianceList: boolean;
  canEditWorkers: boolean;
  canUpdateMembership: boolean;
  canSeeStaffingRequestRates: boolean;
  canShowAdminApprovalsItem: boolean;
  canShowAdminBilling: boolean;
  canShowAdminGroupsTab: boolean;
  canShowAdminScheduleCodesTab: boolean;
  canUpdateAdminScheduleCodes: boolean;
  canShowAdminMembers: boolean;
  canShowAdminOrganizationStructure: boolean;
  canShowAdminRatesOnDemandTab: boolean;
  canShowAdminRatesLongTermTab: boolean;
  canShowAdminStaffingRequestTemplates: boolean;
  canShowAdminSuppliers: boolean;
  canShowAdminTimekeepingHolidays: boolean;
  canShowAdminHolidays: boolean;
  canShowAdminWorksListTemplates: boolean;
  canShowApplications: boolean;
  canShowApplicationsSubmitted: boolean;
  canShowAssignmentTermBillingRules: boolean;
  canShowDisputes: boolean;
  canShowDoNotRehires: boolean;
  canShowOffers: boolean;
  canShowGroupShiftMessaging: boolean;
  canShowInvoices: boolean;
  canShowTimesheetUploads: boolean;
  canShowLongTermAssignmentComments: boolean;
  canShowLongTermAssignments: boolean;
  canShowMissionControl: boolean;
  canShowOrganizationUnitIntegration: boolean;
  canShowOrganizationUnitShiftConfigs: boolean;
  canShowReverseInvoices: boolean;
  canShowShiftTargeting: boolean;
  canShowSidebarAdminItem: boolean;
  canShowSidebarBillingItem: boolean;
  canShowSidebarCandidatesItem: boolean;
  canShowSidebarOnboardingItem: boolean;
  canShowSidebarStaffingItem: boolean;
  canShowSidebarTeamItem: boolean;
  canShowSidebarTimesheetsItem: boolean;
  canShowTermsConditionsLink: boolean;
  canShowShifts: boolean;
  canShowTiering: boolean;
  canShowWorksLists: boolean;
  canShowWorkerStatus: boolean;
  canSubmitSubmittalPacketChangeRequest: boolean;
  canUpdateOffers: boolean;
  canUpdateApplications: boolean;
  canUpdateStaffingRequests: boolean;
  canUpdateAdminUsersUnits: boolean;
  canUpdateWorkerStatus: boolean;
  canUpdateStaffingRequestAssignments: boolean;
  canWithdrawApplications: boolean;
  canShowOrganizationOnDemandSettings: boolean;
  canShiftGapTagCreate: boolean;
  canShiftGapTagUpdate: boolean;
  canUpdateOrganizationBusiness: boolean;
  entity: MembershipEntityType;
  worksUser: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    saveLastVisitedPage: boolean | null;
    timeZone: string;
    initials: string;
    email: string;
    intercomUserHash: string;
  };
};
